import { apiFetch } from "./client";
import type { DocumentTemplateOption } from "./documentTemplates";

export type ContractStatus =
  | "DRAFT"
  | "SENT_TO_CUSTOMER"
  | "CUSTOMER_VIEWED"
  | "SIGNED"
  | "ACTIVE"
  | "EXPIRED"
  | "CANCELLED";

export type ContractVersion = {
  id: string;
  versionNo: number;
  filename: string;
  sizeBytes: number;
  contentType: string;
  signed: boolean;
  uploadedBy: string | null;
  uploadedAt: string;
};

export type Contract = {
  id: string;
  companyId: string;
  companyName: string;
  referenceNumber: string;
  title: string;
  status: ContractStatus;
  effectiveDate: string | null;
  expiresAt: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  versionCount: number;
  latestVersion: ContractVersion | null;
  /**
   * Terms of the contract. A contract from the ERP brings them from there; a contract made in the
   * portal carries whatever was entered with it. Empty only when nobody filled them in.
   */
  currency: string | null;
  incoterm: string | null;
  incotermPlace: string | null;
  paymentTerms: string | null;
  signedOn: string | null;
  contactPerson: string | null;
  note: string | null;
  discountPct: number | null;
  /**
   * What was contracted: goods, quantity and price. From the ERP for a contract from there, as
   * entered for a contract made in the portal; empty when no line was entered.
   */
  lines: ContractLine[];
  /** True for a contract carried over from the ERP: the portal shows it, the ERP keeps it. */
  fromErp?: boolean;
  /** The ERP's document number (1601-…), by which the ERP picks the contract. */
  erpDocumentNumber?: string | null;
  /** The number of the contract this one continues (in the ERP a contract is changed by a new one). */
  previousReferenceNumber?: string | null;
};

/**
 * Šta se šalje pri unosu ugovora.
 *
 * Ista polja koja ugovor donosi iz ERP-a: ugovor nastao u portalu ne sme da bude siromašniji od
 * uvezenog, jer ide u iste knjige.
 */
export type ContractInput = {
  effectiveDate?: string | null;
  expiresAt?: string | null;
  currency?: string | null;
  incoterm?: string | null;
  incotermPlace?: string | null;
  paymentTerms?: string | null;
  signedOn?: string | null;
  contactPerson?: string | null;
  note?: string | null;
  discountPct?: number | null;
  lines?: ContractLineInput[];
};

/** Stavka pri unosu: artikal iz kataloga nije obavezan, ime jeste. */
export type ContractLineInput = {
  productId?: string | null;
  productName: string;
  quantity?: number | null;
  unit?: string | null;
  unitPrice?: number | null;
};

/** Jedna stavka ugovora: roba, koliko i po kojoj ceni. */
export type ContractLine = {
  id: string;
  position: number;
  productId: string | null;
  productName: string | null;
  /** Prazno na okvirnom ugovoru, koji vezuje cenu a ne obim. */
  quantity: number | null;
  unit: string | null;
  unitPrice: number | null;
};

/** Šta je prodaja ugovorila za period i koliko je od toga isporučeno. */
export type ContractOverview = {
  od: string;
  doDatuma: string;
  ukupno: ContractTotals;
  poLiniji: ContractGrupa[];
  poArtiklu: ContractGrupa[];
  poKupcu: ContractGrupa[];
  poUgovoru: ContractRedUgovora[];
};

export type ContractTotals = {
  ugovorenaKolicina: number | null;
  ugovorenaVrednost: number | null;
  isporucenaKolicina: number | null;
  isporucenaVrednost: number | null;
  /** Što je ugovoreno a nije isporučeno; obaveza koja stoji. */
  preostalaKolicina: number | null;
  preostalaVrednost: number | null;
  /** Prazno kad nema ugovorenog; nula bi tvrdila da nije isporučeno ništa. */
  realizacijaPct: number | null;
  /** Koliki je deo roka protekao; bez toga realizacija nema meru. */
  protekloPct: number | null;
  ugovora: number;
  kupaca: number;
  artikala: number;
  isticuUskoro: number;
  ugovorenaVrednostPrethodna: number | null;
};

export type ContractGrupa = {
  kljuc: string;
  naziv: string;
  ugovorenaKolicina: number | null;
  ugovorenaVrednost: number | null;
  isporucenaKolicina: number | null;
  isporucenaVrednost: number | null;
  preostalaKolicina: number | null;
  preostalaVrednost: number | null;
  realizacijaPct: number | null;
  protekloPct: number | null;
  ugovora: number;
};

/** Brojevi za jedan ugovor iz spiska. */
export type ContractRedUgovora = {
  ugovorId: string;
  /** Ko je i šta je: red se mora pročitati i bez drugog upita. */
  broj: string | null;
  naslov: string | null;
  kupacId: string | null;
  kupac: string | null;
  istice: string | null;
  ugovorenaKolicina: number | null;
  ugovorenaVrednost: number | null;
  isporucenaKolicina: number | null;
  isporucenaVrednost: number | null;
  realizacijaPct: number | null;
  protekloPct: number | null;
  danaDoIsteka: number | null;
  /** Koliko ugovora deli isporuku za isti par kupac i artikal; 1 znači da je samo ovaj. */
  deliSa: number;
};

const q = (params: Record<string, string | undefined>) => {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => v && usp.set(k, v));
  const s = usp.toString();
  return s ? `?${s}` : "";
};

async function uploadVersionMultipart(contractId: string, file: File, markSigned: boolean) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(`/api/contracts/${contractId}/versions?markSigned=${markSigned}`, {
    method: "POST",
    credentials: "include",
    body: fd,
  });
  if (!res.ok) {
    let body: { code?: string; message?: string } = {};
    try { body = (await res.json()) as { code?: string; message?: string }; } catch { /* ignore */ }
    throw { status: res.status, code: body.code ?? `HTTP_${res.status}`, message: body.message ?? res.statusText };
  }
  return res.json() as Promise<ContractVersion>;
}

/**
 * The ERP's incoterms (by code) and payment terms (by text) a contract picks from, and the places of delivery on the
 * ERP's contracts it is offered; a place off that list is still taken. The payment terms come for the customer: those
 * offered (in the customer's language and in use, the most used first), then the rest of the ERP's list as known only.
 */
export type ContractErpTerms = {
  incoterms: { code: string; name: string }[];
  paymentTerms: { name: string; mostUsed: boolean; offered: boolean }[];
  deliveryPlaces: string[];
};

export const contractsApi = {
  overview: (year?: number, locale?: string) =>
    apiFetch<ContractOverview>(`/contracts/overview${q({ year: year?.toString(), locale })}`),
  list: (params: { companyId?: string; status?: ContractStatus } = {}) =>
    apiFetch<Contract[]>(`/contracts${q(params)}`),
  get: (id: string) => apiFetch<Contract>(`/contracts/${id}`),
  erpTerms: (companyId?: string) => apiFetch<ContractErpTerms>(`/contracts/erp-terms${q({ companyId })}`),
  versions: (id: string) => apiFetch<ContractVersion[]>(`/contracts/${id}/versions`),
  presign: (id: string, versionId: string) =>
    apiFetch<{ url: string; ttlSeconds: string }>(`/contracts/${id}/versions/${versionId}/download-url`),

  create: (body: ContractInput & { companyId: string; referenceNumber: string; title: string }) =>
    apiFetch<Contract>("/contracts", { method: "POST", body }),

  /** Izostavljeno polje se ne dira; prazna lista stavki je stvarna izmena, a ne izostanak. */
  update: (id: string, body: ContractInput & { title?: string }) =>
    apiFetch<Contract>(`/contracts/${id}`, { method: "PUT", body }),

  transition: (id: string, to: ContractStatus) =>
    apiFetch<Contract>(`/contracts/${id}/transition`, { method: "POST", body: { to } }),

  uploadVersion: (id: string, file: File, markSigned = false) => uploadVersionMultipart(id, file, markSigned),

  /** The sales contract templates in use, to make the contract's Word from. */
  templates: () => apiFetch<DocumentTemplateOption[]>("/contracts/templates"),
  /** A new version: the template filled with the contract's data, as a Word file. */
  versionFromTemplate: (id: string, templateId: string) =>
    apiFetch<ContractVersion>(`/contracts/${id}/versions/from-template`, { method: "POST", body: { templateId } }),
};
